import React from 'react';
import { motion } from 'framer-motion';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

const Services = () => {
  const navigate = useNavigate();
  const { lang = 'en' } = useParams();
  const { t } = useTranslation('home');

  // Helper to get language-prefixed path
  const getLocalizedPath = (path) => `/${lang}${path}`;

  const services = [
    {
      id: 'e-commerce-solutions',
      icon: (
        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
        </svg>
      ),
      titleKey: 'services.items.ecommerce.title',
      descriptionKey: 'services.items.ecommerce.description',
    },
    {
      id: 'mobile-app-development',
      icon: (
        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
        </svg>
      ),
      titleKey: 'services.items.mobile.title',
      descriptionKey: 'services.items.mobile.description',
    },
    {
      id: 'web-development',
      icon: (
        <img src="/images/logos_svg/www.svg" alt="Web Development" className="w-8 h-8" />
      ),
      titleKey: 'services.items.web.title',
      descriptionKey: 'services.items.web.description',
    },
  ];

  return (
    <section id="services" className="bg-white min-h-screen py-20 pb-32 mb-20 flex items-center" data-section="services">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="flex flex-col md:flex-row justify-between items-start gap-6 mb-16"
        >
          <div>
            <p className="text-secondary-accent text-sm font-semibold mb-2 uppercase tracking-wider flex items-center gap-2">
              <span className="w-2 h-2 bg-secondary-accent rounded-full"></span>
              {t('services.badge')}
            </p>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-text-primary mb-4">
              {t('services.title')}
            </h2>
          </div>
          <div className="text-left md:text-right max-w-md">
            <div
              onClick={() => navigate(getLocalizedPath('/services'))}
              className="inline-block bg-white text-text-primary border-2 border-text-primary rounded-2xl px-4 py-2 mb-3 cursor-pointer hover:bg-text-primary hover:text-white transition-all duration-300"
            >
              <p className="text-sm font-medium">
                {t('services.seeAll')}
              </p>
            </div>
            <p className="text-base sm:text-lg text-grey-accent">
              {t('services.description')}
            </p>
          </div>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {services.map((service, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              className="group cursor-pointer"
              onClick={() => navigate(getLocalizedPath(`/services/${service.id}`))}
            >
              {/* Card */}
              <div className="bg-[#F9F9F9] rounded-2xl p-8 group-hover:bg-text-primary transition-all duration-300 relative overflow-hidden aspect-square flex flex-col hover:scale-[1.02] scale-x-[1.02]">
                {/* Icon - Top Left inside card */}
                <div className="absolute top-6 left-6">
                  <div className="w-16 h-16 bg-[#EDEDED] rounded-full flex items-center justify-center text-text-primary group-hover:bg-[#EDEDED] transition-all duration-300">
                    {service.icon}
                  </div>
                </div>

                {/* Arrow Icon - Top Right, pointing 45 degrees upward */}
                <div className="absolute top-6 right-6">
                  <div className="w-10 h-10 flex items-center justify-center">
                    <svg className="w-6 h-6 text-text-primary group-hover:text-white transition-colors duration-300 transform -rotate-45" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                    </svg>
                  </div>
                </div>

                {/* Spacer to push content down */}
                <div className="flex-grow"></div>

                {/* Title and Description - Bottom */}
                <div>
                  <h3 className="text-2xl font-bold text-text-primary group-hover:text-white mb-4 transition-colors duration-300">
                    {t(service.titleKey)}
                  </h3>

                  {/* Description */}
                  <p className="text-grey-accent group-hover:text-white/80 text-base leading-relaxed transition-colors duration-300">
                    {t(service.descriptionKey)}
                  </p>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Services;
